import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Users, UserCheck, UserMinus, Clock } from 'lucide-react';
import Sidebar from './Sidebar';
import './Dashboard.css';

const CompanyOverview = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deliveryAddress, setDeliveryAddress] = useState(null);
    const [logoSrcIndex, setLogoSrcIndex] = useState(0);

    const userEmail = localStorage.getItem('email') || '';
    const avatarInitials = userEmail
        ? userEmail.split('@')[0].slice(0, 2).toUpperCase()
        : '??';

    const RESTRICTED_DOMAINS = ['project-a.com', 'a11.global', 'a11.com'];
    const domain = userEmail.split('@')[1] || '';
    const isRestricted = RESTRICTED_DOMAINS.includes(domain);
    const domainCompanyName = domain ? domain.split('.')[0].replace(/^\w/, c => c.toUpperCase()) : 'Your Company';
    const [backendCompanyName, setBackendCompanyName] = useState(null);
    const companyName = backendCompanyName || domainCompanyName;

    const logoSources = domain ? [
        `https://logo.clearbit.com/${domain}?size=400`,
        `https://cdn.brandfetch.io/${domain}/w/400/h/400/logo`,
        `https://cdn.brandfetch.io/${domain}/w/400/h/400`,
    ] : [];

    const currentLogoSrc = logoSources[logoSrcIndex] || null;

    useEffect(() => {
        const token = localStorage.getItem('x-cc-auth');
        if (!token) {
            navigate('/');
            return;
        }
        fetchUsers();
        fetchAddress(token);
    }, [navigate]);

    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem('x-cc-auth');
            const response = await fetch('https://api.chefcoco.com/app/admin/all/customer', {
                headers: {
                    'Content-Type': 'application/json',
                    'X-CC-AUTH': `${token}`,
                },
            });
            if (!response.ok) throw new Error('Failed to fetch users');
            const data = await response.json();
            setItems(data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchAddress = async (token) => {
        try {
            const v1Res = await fetch('https://api.chefcoco.com/app/v1', {
                headers: { 'X-CC-AUTH': token },
            });
            const v1Data = await v1Res.json();
            if (v1Data.companyName) setBackendCompanyName(v1Data.companyName);
            const customerId = v1Data.customerId;
            if (!customerId) return;

            const addrRes = await fetch(`https://api.chefcoco.com/app/${customerId}/address`, {
                headers: { 'X-CC-AUTH': token },
            });
            const addrData = await addrRes.json();
            const addresses = (addrData.addresses || addrData || []).filter(a => !a.deleted);
            if (addresses.length === 1) {
                setDeliveryAddress(addresses[0].formattedAddress || null);
            }
        } catch (err) {
            console.error('Address fetch failed:', err);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('x-cc-auth');
        window.location.href = '/';
    };

    const activeUsers = items.filter(i => i.status === 'Active').length;
    const deactivatedUsers = items.filter(i => i.status === 'Deactivated').length;
    const deactivationRequestedUsers = items.filter(i => i.status === 'Deactivation Requested').length;
    const totalUsers = items.length;

    return (
        <div className="dashboard-container">
            <Sidebar
                currentPath="/overview"
                onNavigate={(path) => navigate(path)}
                onLogout={handleLogout}
            />

            <div className="main-content">
                {/* Topbar */}
                <div className="topbar">
                    <div className="topbar-title">
                        <h2>Company Overview</h2>
                        <span className="topbar-badge">Client Portal</span>
                    </div>
                    <button className="avatar-btn" title={userEmail}>{avatarInitials}</button>
                </div>

                <div className="content-area">

                    {/* Company Identity Card */}
                    <div className="section-card" style={{ marginBottom: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                            <div style={{
                                width: '80px', height: '80px',
                                borderRadius: '18px',
                                background: '#f0f2f0',
                                border: '2px solid #e5e7eb',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                                overflow: 'hidden',
                            }}>
                                {currentLogoSrc ? (
                                    <img
                                        key={currentLogoSrc}
                                        src={currentLogoSrc}
                                        alt={`${companyName} Logo`}
                                        style={{ width: '56px', height: '56px', objectFit: 'contain' }}
                                        onError={() => setLogoSrcIndex(i => i + 1)}
                                        onLoad={(e) => {
                                            // Clearbit/Brandfetch return a tiny placeholder (≤16px) when no logo found
                                            if (e.target.naturalWidth <= 16 || e.target.naturalHeight <= 16) {
                                                setLogoSrcIndex(i => i + 1);
                                            }
                                        }}
                                    />
                                ) : (
                                    <div style={{
                                        width: '56px', height: '56px',
                                        borderRadius: '12px',
                                        background: '#1a3a28',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: '#fff', fontSize: '22px', fontWeight: 700,
                                        letterSpacing: '-1px',
                                    }}>
                                        {companyName.slice(0, 2).toUpperCase()}
                                    </div>
                                )}
                            </div>
                            <div>
                                <div style={{ fontSize: '22px', fontWeight: 700, color: '#111', marginBottom: '4px' }}>
                                    {companyName}
                                </div>
                                {deliveryAddress && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280', fontSize: '13.5px' }}>
                                        <MapPin size={14} />
                                        <span>{deliveryAddress}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Stat Cards */}
                    {!isRestricted && <div className="stat-cards" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: '20px' }}>
                        <div className="stat-card">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                <div style={{
                                    width: '36px', height: '36px', borderRadius: '10px',
                                    background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    <UserCheck size={18} color="#16a34a" />
                                </div>
                                <div className="stat-card-label" style={{ margin: 0 }}>Active Users</div>
                            </div>
                            <div className="stat-card-value">{loading ? '—' : activeUsers}</div>
                            <div className="stat-card-sub green">Currently active</div>
                        </div>
                        <div className="stat-card">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                <div style={{
                                    width: '36px', height: '36px', borderRadius: '10px',
                                    background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    <Clock size={18} color="#ea580c" />
                                </div>
                                <div className="stat-card-label" style={{ margin: 0 }}>Pending Removal</div>
                            </div>
                            <div className="stat-card-value">{loading ? '—' : deactivationRequestedUsers}</div>
                            <div className="stat-card-sub" style={{ color: '#ea580c' }}>Deactivation requested</div>
                        </div>
                        <div className="stat-card">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                <div style={{
                                    width: '36px', height: '36px', borderRadius: '10px',
                                    background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    <UserMinus size={18} color="#dc2626" />
                                </div>
                                <div className="stat-card-label" style={{ margin: 0 }}>Deactivated</div>
                            </div>
                            <div className="stat-card-value">{loading ? '—' : deactivatedUsers}</div>
                            <div className="stat-card-sub" style={{ color: '#dc2626' }}>No longer active</div>
                        </div>
                        <div className="stat-card">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                <div style={{
                                    width: '36px', height: '36px', borderRadius: '10px',
                                    background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    <Users size={18} color="#2563eb" />
                                </div>
                                <div className="stat-card-label" style={{ margin: 0 }}>Total Users</div>
                            </div>
                            <div className="stat-card-value">{loading ? '—' : totalUsers}</div>
                            <div className="stat-card-sub">All statuses</div>
                        </div>
                    </div>}

                    {/* Account Manager Card */}
                    <div className="section-card">
                        <h3 className="section-card-title" style={{ marginBottom: '16px' }}>Your Account Manager</h3>
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '16px',
                            padding: '16px',
                            background: '#f9fafb',
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb',
                        }}>
                            <div style={{
                                width: '52px', height: '52px',
                                borderRadius: '50%',
                                background: '#1a3a28',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#fff', fontSize: '18px', fontWeight: 700,
                                flexShrink: 0,
                            }}>
                                K
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '16px', fontWeight: 700, color: '#111', marginBottom: '8px' }}>
                                    Kristina
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <a
                                        href="mailto:kristina@chefcoco.com"
                                        style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                                            color: '#1a3a28', fontSize: '13.5px', textDecoration: 'none', fontWeight: 500,
                                        }}
                                    >
                                        <Mail size={14} />
                                        kristina@chefcoco.com
                                    </a>
                                    <a
                                        href="tel:+4917670806926"
                                        style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                                            color: '#1a3a28', fontSize: '13.5px', textDecoration: 'none', fontWeight: 500,
                                        }}
                                    >
                                        <Phone size={14} />
                                        +49 176 70806926
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default CompanyOverview;
